package ai.jessie.web.pages.profile

import ai.jessie.web.onboarding.OnboardingProblem
import ai.jessie.web.onboarding.onboardingProblem
import ai.jessie.web.onboarding.validOrganisationId
import ai.jessie.web.profile.EmailChangeState
import ai.jessie.web.profile.LocalEmailChangePort
import ai.jessie.web.profile.LocalProfilePort
import ai.jessie.web.profile.ProfileState
import ai.jessie.web.profile.emailChangeSection
import ai.jessie.web.profile.ownProfile
import ai.jessie.web.theme.AlertStyle
import ai.jessie.web.theme.appFrame
import ai.jessie.web.theme.card
import ai.jessie.web.theme.colorModeChoice
import ai.jessie.web.theme.profileHref
import ai.jessie.web.theme.settingsSideGroups
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.key
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import com.varabyte.kobweb.core.AppGlobals
import com.varabyte.kobweb.core.Page
import com.varabyte.kobweb.core.PageContext
import com.varabyte.kobweb.core.isExporting
import com.varabyte.kobweb.silk.style.toAttrs
import kotlinx.browser.window
import org.jetbrains.compose.web.dom.A
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Text

// UC-026. `/profile` is global-only (A2); `?organisationId=` selects one membership. State is keyed
// by that selection, so changing it discards membership values together with any proposal.
@Page
@Composable
fun profilePage(ctx: PageContext) {
    val organisationId = ctx.route.params["organisationId"]
    if (organisationId != null && !validOrganisationId(organisationId)) {
        // No organisation is trusted from an unusable link, so no organisation navigation either.
        appFrame(null, profileHref(null), PROFILE_PAGE_TITLE) {
            Div(attrs = AlertStyle.toAttrs()) {
                onboardingProblem(
                    OnboardingProblem(
                        "Ungültiger Organisationslink.",
                        destination = "/profile",
                        destinationLabel = "Profil ohne Organisation öffnen",
                    ),
                    busy = false,
                )
            }
        }
    } else key(organisationId) { profileContent(organisationId) }
}

@Composable
private fun profileContent(organisationId: String?) {
    val port = LocalProfilePort.current
    val emailPort = LocalEmailChangePort.current
    val scope = rememberCoroutineScope()
    val model = remember(port, scope, organisationId) { ProfileState(port, scope, organisationId) }
    val email =
        remember(emailPort, scope) {
            EmailChangeState(emailPort, scope) { window.location.assign(it) }
        }
    LaunchedEffect(model) { if (!AppGlobals.isExporting) model.refresh() }
    val authenticationRequired =
        model.state.error?.authenticationRequired == true ||
            email.state.error?.authenticationRequired == true
    // `18-EinstellungenProfil.png`; the organisation caption uses the returned membership only.
    appFrame(
        organisationId,
        profileHref(organisationId),
        PROFILE_PAGE_TITLE,
        authenticationRequired = authenticationRequired,
        sideGroups =
            settingsSideGroups(organisationId, model.state.current?.membership?.organisationName),
    ) {
        ownProfile(model.state, model.actions)
        if (!authenticationRequired) {
            // Device preference only; nothing is sent to the server.
            card("Darstellung", "Gilt für dieses Gerät.") { colorModeChoice() }
            // Reachable without onboarding or membership (BR-189, approved B6), never without a
            // session.
            emailChangeSection(model.state.current?.verifiedEmail, email.state, email.actions)
        }
        P {
            if (organisationId != null)
                A(href = "/organisations/$organisationId") { Text("Zurück zur Organisation") }
            else A(href = "/organisations") { Text("Zur Organisationsauswahl") }
        }
    }
}

// `18-EinstellungenProfil.png`: the page belongs to the settings area.
private const val PROFILE_PAGE_TITLE = "Einstellungen"
